import type { PlanoEscala, PlanoEscalaElegida, PlanoInventario } from './domain.js';

/**
 * QUÉ FACTOR usar para convertir una magnitud del dibujo (unidades del CAD) a METROS. La política, en UN
 * solo lugar:
 *
 *  1. Si el usuario eligió la unidad (`elegida`, de `project_files.escala_dibujo_elegida`) → esa.
 *  2. Si el inventario trae `escala` (lector desde el paso 2 del plan de medición DWG) → lo que resolvió:
 *     'definida' da su factor; 'dudosa' da `factor: null` + `dudosa: true` con las opciones, y NO se
 *     mide hasta que el usuario elija (nunca se muestra una cantidad con una unidad adivinada).
 *  3. Inventario viejo (sin `escala`): la política anterior —cotas con confianza ≥ 0.5 o, si no,
 *     `$INSUNITS`— para no cambiar lo que ya se veía de un plano generado antes.
 *
 * Función pura: la comparten el panel de propiedades y el cálculo de cantidades.
 */
export interface FactorDeEscala {
  /** Factor unidad-de-dibujo → metro, o null si no se conoce la escala. */
  factor: number | null;
  /** De dónde salió el factor. */
  fuente: 'usuario' | 'insunits' | 'dibujo' | 'cotas' | null;
  /**
   * true = conviene que el ingeniero confirme la unidad: la escala quedó dudosa, o (inventario viejo)
   * las cotas contradicen al `$INSUNITS`.
   */
  revisar: boolean;
  /** true = la escala es dudosa y hay que preguntarle la unidad al usuario (ver `escala.opciones`). */
  dudosa: boolean;
  /** La escala resuelta por el lector, si el inventario la trae (opciones y consecuencias incluidas). */
  escala: PlanoEscala | null;
}

/** Umbral de confianza de las cotas en inventarios VIEJOS (sin `escala`): la política de antes del paso 2. */
export const ESCALA_COTAS_CONFIANZA_MIN = 0.5;

const valido = (f: unknown): f is number => typeof f === 'number' && Number.isFinite(f) && f > 0;

export function factorDeEscala(
  inv: PlanoInventario | null | undefined,
  elegida?: PlanoEscalaElegida | null,
): FactorDeEscala {
  const escala = inv?.escala ?? null;

  // 1) La respuesta del usuario manda.
  if (elegida && valido(elegida.factor)) {
    return { factor: elegida.factor, fuente: 'usuario', revisar: false, dudosa: false, escala };
  }

  // 2) Lo que resolvió el lector.
  if (escala) {
    if (escala.estado === 'definida' && valido(escala.factor)) {
      const fuente = escala.fuente === 'usuario' || escala.fuente === 'insunits' ? escala.fuente : 'dibujo';
      return { factor: escala.factor, fuente, revisar: false, dudosa: false, escala };
    }
    return { factor: null, fuente: null, revisar: true, dudosa: true, escala };
  }

  // 3) Inventario viejo.
  const cotas = inv?.escala_por_cotas;
  const revisar = cotas?.concuerda_insunits === false;
  if (cotas && valido(cotas.factor) && cotas.confianza >= ESCALA_COTAS_CONFIANZA_MIN) {
    return { factor: cotas.factor, fuente: 'cotas', revisar, dudosa: false, escala: null };
  }
  const insunits = inv?.unidades?.factor_a_metro;
  if (valido(insunits)) {
    return { factor: insunits, fuente: 'insunits', revisar, dudosa: false, escala: null };
  }
  return { factor: null, fuente: null, revisar, dudosa: false, escala: null };
}

/**
 * ¿Hay que preguntarle la unidad al usuario? Sí solo si la escala resuelta al subir (o la del
 * inventario) es dudosa y todavía no contestó. Es el único criterio: la pantalla no decide otro.
 */
export function hayQuePreguntarUnidad(
  escala: PlanoEscala | null | undefined,
  elegida: PlanoEscalaElegida | null | undefined,
): boolean {
  if (elegida && valido(elegida.factor)) return false;
  return !!escala && escala.estado === 'dudosa' && escala.opciones.length > 0;
}
